import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

type PriceOption = number | 'Custom';

// Gift card options
const giftCardOptions = [
  {
    id: 'classic',
    name: 'Classic Elegance',
    description: 'Our timeless gift card with a classic floral design, perfect for any occasion.',
    price: [50, 100, 150, 200, 'Custom'] as const,
    image: 'https://images.pexels.com/photos/6707631/pexels-photo-6707631.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'deluxe',
    name: 'Deluxe Package',
    description: 'A premium gift card presented in a luxurious gift box with botanical bath salts.',
    price: [100, 150, 200, 250, 'Custom'] as const,
    image: 'https://images.pexels.com/photos/6707504/pexels-photo-6707504.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'digital',
    name: 'Digital Instant',
    description: 'Send instantly via email with a personalized message, perfect for last-minute gifts.',
    price: [50, 75, 100, 150, 200, 'Custom'] as const,
    image: 'https://images.pexels.com/photos/6956883/pexels-photo-6956883.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
];

// Package suggestions
const suggestedPackages = [
  {
    id: 'relaxation',
    name: 'Relaxation Retreat',
    description: 'Perfect for anyone who needs to unwind and destress with our signature massage and facial.',
    price: 180,
    duration: '2.5 hours',
    image: 'https://images.pexels.com/photos/3757952/pexels-photo-3757952.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'couples',
    name: 'Couples Escape',
    description: 'A romantic experience for two featuring side-by-side massages and complimentary champagne.',
    price: 280,
    duration: '2 hours',
    image: 'https://images.pexels.com/photos/3188/love-romantic-bath-candlelight.jpg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'complete',
    name: 'Complete Rejuvenation',
    description: 'Our most comprehensive package including massage, facial, body wrap, and more.',
    price: 350,
    duration: '4 hours',
    image: 'https://images.pexels.com/photos/3865557/pexels-photo-3865557.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
];

interface GiftCardFormData {
  type: string;
  amount: PriceOption;
  customAmount: string;
  recipientName: string;
  recipientEmail: string;
  senderName: string;
  message: string;
  deliveryDate: string;
}

function GiftCard() {
  const { t } = useTranslation(['common']);
  const [selectedCard, setSelectedCard] = useState(giftCardOptions[0].id);
  const [selectedAmount, setSelectedAmount] = useState<PriceOption>(100);
  const [customAmount, setCustomAmount] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  const [formData, setFormData] = useState<GiftCardFormData>({
    type: giftCardOptions[0].id,
    amount: 100,
    customAmount: '',
    recipientName: '',
    recipientEmail: '',
    senderName: '',
    message: '',
    deliveryDate: '',
  });
  
  const handleCardSelect = (cardId: string) => {
    setSelectedCard(cardId);
    setFormData({...formData, type: cardId});
  };
  
  const handleAmountSelect = (amount: PriceOption) => {
    setSelectedAmount(amount);
    setFormData({...formData, amount});
    
    if (amount !== 'Custom') {
      setCustomAmount('');
      setFormData({...formData, amount, customAmount: ''});
    }
  };
  
  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    if (/^\d*$/.test(value)) {
      setCustomAmount(value);
      setFormData({...formData, customAmount: value});
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({...formData, [name]: value});
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Gift card order:', formData);
    setFormSubmitted(true);
    
    // Reset form after submission (in a real app, this would happen after successful API call)
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({
        type: giftCardOptions[0].id,
        amount: 100,
        customAmount: '',
        recipientName: '',
        recipientEmail: '',
        senderName: '',
        message: '',
        deliveryDate: '',
      });
      setSelectedCard(giftCardOptions[0].id);
      setSelectedAmount(100);
      setCustomAmount('');
    }, 5000);
  };
  
  // Get selected card data
  const cardData = giftCardOptions.find(card => card.id === selectedCard) || giftCardOptions[0];
  
  // Calculate final amount
  const finalAmount = selectedAmount === 'Custom' ? (customAmount ? parseInt(customAmount) : 0) : selectedAmount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[#232347]/60 z-10"></div>
        <div className="relative z-20 text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-serif">{t('nav.giftCard')}</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">{t('common.giftCardSubtitle')}</p>
        </div>
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/6707578/pexels-photo-6707578.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
            alt="Gift box with bow" 
            className="w-full h-full object-cover"
          />
        </div>
      </section>
      
      <div className="container mx-auto py-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Gift Card Options */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">{t('common.selectGiftCard')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {giftCardOptions.map((card) => (
                <div 
                  key={card.id}
                  className={`bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer border-2 ${selectedCard === card.id ? 'border-[#B2D8C5]' : 'border-transparent'}`}
                  onClick={() => handleCardSelect(card.id)}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={card.image} 
                      alt={card.name} 
                      className="w-full h-full object-cover"
                    />
                    {selectedCard === card.id && (
                      <div className="absolute top-4 right-4 bg-[#B2D8C5] rounded-full p-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#232347] mb-2">{card.name}</h3>
                    <p className="text-[#232347]/70 text-sm mb-4">{card.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {card.price.map((amount, idx) => (
                        <span 
                          key={idx} 
                          className={`px-3 py-1 rounded-full text-sm font-medium ${card.id === selectedCard && amount === selectedAmount ? 'bg-[#B2D8C5] text-white' : 'bg-[#F7CAC9]/20 text-[#232347]'}`}
                        >
                          {amount === 'Custom' ? 'Custom Amount' : `$${amount}`}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Gift Card Customization */}
          <section className="mb-20">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#232347] mb-8 text-center">Customize Your Gift</h2>
              
              {formSubmitted ? (
                <div className="bg-[#B2D8C5]/20 border border-[#B2D8C5] text-[#232347] rounded-lg p-8 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-[#B2D8C5] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-2xl font-semibold mb-2">Thank You for Your Order!</h3>
                  <p className="text-[#232347]/80 mb-4">Your gift card has been processed. A confirmation email will be sent shortly.</p>
                  <p className="text-[#232347]/80">Order Amount: <span className="font-semibold">${finalAmount}</span></p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
                  {/* Step 1: Select Amount */}
                  <div className="mb-10">
                    <h3 className="text-xl font-semibold text-[#232347] mb-4">1. Select Amount</h3>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
                      {cardData.price.map((amount, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`py-2 rounded-md text-lg font-medium transition-colors ${amount === selectedAmount ? 'bg-[#B2D8C5] text-white' : 'bg-white border border-[#B2D8C5]/50 text-[#232347] hover:bg-[#B2D8C5]/10'}`}
                          onClick={() => handleAmountSelect(amount as PriceOption)}
                        >
                          {amount === 'Custom' ? 'Custom' : `$${amount}`}
                        </button>
                      ))}
                    </div>
                    {selectedAmount === 'Custom' && (
                      <div className="flex items-center">
                        <span className="text-xl font-medium mr-2">$</span>
                        <input
                          type="text"
                          value={customAmount}
                          onChange={handleCustomAmountChange}
                          placeholder="Enter amount"
                          className="w-full max-w-xs px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Step 2: Recipient Information */}
                  <div className="mb-10">
                    <h3 className="text-xl font-semibold text-[#232347] mb-4">2. Recipient Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="recipientName" className="block text-[#232347] font-medium mb-2">
                          Recipient's Name
                        </label>
                        <input
                          type="text"
                          id="recipientName"
                          name="recipientName"
                          value={formData.recipientName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                        />
                      </div>
                      <div>
                        <label htmlFor="recipientEmail" className="block text-[#232347] font-medium mb-2">
                          Recipient's Email
                        </label>
                        <input
                          type="email"
                          id="recipientEmail"
                          name="recipientEmail"
                          value={formData.recipientEmail}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="senderName" className="block text-[#232347] font-medium mb-2">
                        Your Name
                      </label>
                      <input
                        type="text"
                        id="senderName"
                        name="senderName"
                        value={formData.senderName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                      />
                    </div>
                  </div>
                  
                  {/* Step 3: Personalize */}
                  <div className="mb-10">
                    <h3 className="text-xl font-semibold text-[#232347] mb-4">3. Personalize Your Gift</h3>
                    <div className="mb-6">
                      <label htmlFor="message" className="block text-[#232347] font-medium mb-2">
                        Personal Message (Optional)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Add a personal message for the recipient..."
                        className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                      ></textarea>
                    </div>
                    {selectedCard === 'digital' && (
                      <div>
                        <label htmlFor="deliveryDate" className="block text-[#232347] font-medium mb-2">
                          Delivery Date
                        </label>
                        <input
                          type="date"
                          id="deliveryDate"
                          name="deliveryDate"
                          value={formData.deliveryDate}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-[#B2D8C5] rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Order Summary */}
                  <div className="bg-[#F8F6F2] rounded-lg p-6 mb-8">
                    <h3 className="text-xl font-semibold text-[#232347] mb-4">Order Summary</h3>
                    <div className="flex justify-between mb-2">
                      <span className="text-[#232347]/80">Gift Card Type:</span>
                      <span className="font-medium">{cardData.name}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-[#232347]/80">Amount:</span>
                      <span className="font-medium">${finalAmount}</span>
                    </div>
                    <hr className="my-4 border-[#232347]/20" />
                    <div className="flex justify-between">
                      <span className="text-[#232347] font-semibold">Total:</span>
                      <span className="text-[#232347] font-semibold">${finalAmount}</span>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-[#B2D8C5] hover:bg-[#88bfa3] text-white font-semibold py-3 px-6 rounded-md transition-colors"
                    disabled={selectedAmount === 'Custom' && !customAmount}
                  >
                    Purchase Gift Card
                  </button>
                </form>
              )}
            </div>
          </section>
          
          {/* Popular Packages Section */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">Popular Gift Packages</h2>
            <p className="text-[#232347]/80 mb-12 text-center max-w-3xl mx-auto">
              Looking for the perfect spa experience? Our curated packages make excellent gifts for any occasion.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {suggestedPackages.map((pkg) => (
                <div 
                  key={pkg.id}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={pkg.image} 
                      alt={pkg.name} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4 bg-[#F7CAC9] text-white px-3 py-1 rounded-full text-sm font-medium">
                      ${pkg.price}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#232347] mb-2">{pkg.name}</h3>
                    <p className="text-[#232347]/70 text-sm mb-3">{pkg.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-[#232347]/80 text-sm">{pkg.duration}</span>
                      <button className="bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* FAQ Section */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">Frequently Asked Questions</h2>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-md p-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">How long are gift cards valid?</h3>
                  <p className="text-[#232347]/70">Our gift cards are valid for one year from the date of purchase, giving your recipient plenty of time to plan their perfect spa day.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">Can gift cards be used for any service?</h3>
                  <p className="text-[#232347]/70">Yes, gift cards can be applied to any of our services or products, including massages, facials, body treatments, and retail items.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">How will the recipient receive their gift card?</h3>
                  <p className="text-[#232347]/70">Physical gift cards can be picked up at our location or mailed to the recipient. Digital gift cards are delivered via email on the date you specify.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">Can I add a personal message?</h3>
                  <p className="text-[#232347]/70">Absolutely! You can add a personalized message during checkout that will be included with your gift card.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#232347] mb-2">What if the recipient wants to use the gift card for a service that costs more?</h3>
                  <p className="text-[#232347]/70">Recipients can apply the gift card value toward any service and pay the difference for services exceeding the gift card amount.</p>
                </div>
              </div>
            </div>
          </section>
          
          {/* Testimonial Section */}
          <section className="mb-20">
            <div className="bg-[#232347]/5 rounded-xl p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#232347] mb-6">What Our Clients Say</h2>
              <p className="text-xl md:text-2xl italic text-[#232347]/80 mb-6 max-w-3xl mx-auto">
                "The gift card I received for my birthday was the perfect present. The massage was incredible, and the staff made me feel so special. I can't wait to return!"
              </p>
              <p className="text-[#B2D8C5] font-medium">— Sarah T., Gift Card Recipient</p>
            </div>
          </section>
          
          {/* Call to Action */}
          <section className="text-center">
            <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#232347] mb-4">Need Help?</h2>
            <p className="text-[#232347]/80 mb-8 max-w-2xl mx-auto">
              Have questions about our gift cards or need assistance with placing an order? Our team is here to help.
            </p>
            <a 
              href="/contact" 
              className="inline-block bg-[#F7CAC9] hover:bg-[#e6b9b8] text-white px-8 py-3 rounded-md font-semibold text-lg transition-colors"
            >
              Contact Us
            </a>
          </section>
        </div>
      </div>
    </div>
  );
}

export default GiftCard;